import { cn } from '../../lib/utils';
import { FORM_RATING_MAX } from '../../types/form-question';
import { Button } from '../shadcn/button';

const SCORES = Array.from({ length: FORM_RATING_MAX }, (_, i) => i + 1);

/**
 * A score from 1 to 5, as a row of buttons — one press, nothing to type.
 *
 * The value is text (`''` for none, `'7'`), like every other control in a
 * form, and the schema turns it into a number. Takes what `Field` hands a
 * control, so it binds the same way:
 * `<Field name="overallRating" label="…"><RatingScale /></Field>`.
 *
 * A radio group to a screen reader: one choice of five, the arrow keys aside.
 */
export function RatingScale({
  id,
  value = '',
  onChange,
  onBlur,
  disabled,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: {
  id?: string;
  value?: string;
  onChange?: (next: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}) {
  return (
    <div
      id={id}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      // One row of five, which leaves room to aim even on a phone.
      className={cn('grid grid-cols-5 gap-2', className)}
    >
      {SCORES.map((score) => {
        const picked = value === String(score);
        return (
          <Button
            key={score}
            type="button"
            role="radio"
            aria-checked={picked}
            variant={picked ? 'default' : 'outline'}
            disabled={disabled}
            onClick={() => onChange?.(String(score))}
            onBlur={onBlur}
          >
            {score}
          </Button>
        );
      })}
    </div>
  );
}
